import { createClient, SupabaseClient } from '@supabase/supabase-js';
import { Database } from '../types/database';

// Initialize Supabase client with environment variables
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';

// Validate that environment variables are set
if (!supabaseUrl || !supabaseAnonKey) {
  console.error('Missing Supabase environment variables. Please check your .env file.');
  throw new Error('Missing required Supabase configuration');
}

// Enhanced Supabase client configuration with security optimizations
export const supabase: SupabaseClient<Database> = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    // Automatically refresh tokens when they expire
    autoRefreshToken: true,
    // Persist session in localStorage with encryption
    persistSession: true,
    // Detect session in URL hash
    detectSessionInUrl: true,
    // Storage key for session data
    storageKey: 'payroll-auth-token',
    // Custom storage implementation with encryption
    storage: {
      getItem: (key: string) => {
        try {
          const item = localStorage.getItem(key);
          if (!item) return null;
          
          // Simple encryption/decryption for demo purposes
          // In production, use a proper encryption library
          const decoded = atob(item);
          return decoded;
        } catch (error) {
          console.error('Error retrieving session:', error);
          return null;
        }
      },
      setItem: (key: string, value: string) => {
        try {
          // Simple encryption for demo purposes
          const encoded = btoa(value);
          localStorage.setItem(key, encoded);
        } catch (error) {
          console.error('Error storing session:', error);
        }
      },
      removeItem: (key: string) => {
        try {
          localStorage.removeItem(key);
        } catch (error) {
          console.error('Error removing session:', error);
        }
      }
    },
    // Flow type for authentication
    flowType: 'pkce'
  },
  // Global configuration
  global: {
    headers: {
      'X-Client-Info': 'payroll-system@1.0.0'
    }
  },
  // Realtime configuration
  realtime: {
    params: {
      eventsPerSecond: 10
    }
  }
});

// Session management utilities
export const sessionManager = {
  // Check if session is valid and not expired
  isSessionValid: async (): Promise<boolean> => {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      if (error || !session) return false;
      
      // Check if token is expired (with 5 minute buffer)
      const expiresAt = session.expires_at;
      const now = Math.floor(Date.now() / 1000);
      const buffer = 5 * 60; // 5 minutes
      
      return expiresAt ? (expiresAt - buffer) > now : false;
    } catch (error) {
      console.error('Error checking session validity:', error);
      return false;
    }
  },
  
  // Force refresh token
  refreshSession: async (): Promise<boolean> => {
    try {
      const { data, error } = await supabase.auth.refreshSession();
      if (error) {
        console.error('Error refreshing session:', error);
        return false;
      }
      return !!data.session;
    } catch (error) {
      console.error('Error refreshing session:', error);
      return false;
    }
  },
  
  // Clear session with comprehensive local-first logout
  clearSession: async (): Promise<void> => {
    try {
      // Force local-only logout to avoid network issues
      console.log('Performing local logout to avoid network errors');
      
      // Clear the session from Supabase client state
      await supabase.auth.signOut({ scope: 'local' });
      
    } catch (error) {
      console.warn('Local logout failed, forcing manual cleanup:', error);
    } finally {
      // Always perform comprehensive local cleanup
      try {
        // Clear all possible auth storage keys
        const keysToRemove = [
          'payroll-user-data',
          'payroll-auth-token',
          'sb-' + supabaseUrl.split('//')[1] + '-auth-token',
          'supabase.auth.token',
          'sb-' + supabaseUrl.replace('https://', '').replace('http://', '') + '-auth-token'
        ];
        
        keysToRemove.forEach(key => {
          try {
            localStorage.removeItem(key);
            sessionStorage.removeItem(key);
          } catch (e) {
            console.warn(`Failed to remove ${key}:`, e);
          }
        });
        
        // Clear any cookies related to auth
        document.cookie.split(';').forEach(cookie => {
          const eqPos = cookie.indexOf('=');
          const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
          if (name.includes('supabase') || name.includes('auth') || name.includes('payroll')) {
            document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
          }
        });
        
        console.log('Local cleanup completed successfully');
      } catch (storageError) {
        console.error('Error during local cleanup:', storageError);
      }
    }
  },
  
  // Get session with automatic refresh if needed
  getValidSession: async () => {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      if (error) throw error;
      
      if (!session) return null;
      
      // Check if token needs refresh
      const isValid = await sessionManager.isSessionValid();
      if (!isValid) {
        const refreshed = await sessionManager.refreshSession();
        if (!refreshed) {
          await sessionManager.clearSession();
          return null;
        }
        // Get the refreshed session
        const { data: { session: newSession } } = await supabase.auth.getSession();
        return newSession;
      }
      
      return session;
    } catch (error) {
      console.error('Error getting valid session:', error);
      return null;
    }
  }
};